import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FolderOpen, Loader2, UserSquare2, Calendar, ArrowRight } from "lucide-react";

/**
 * Fase 5.9: sección para el médico que enumera los pacientes que le
 * compartieron su expediente. Cada fila lleva a la vista de solo lectura
 * en `/medico/pacientes/:patient_id`.
 */
export default function PacientesCompartidosCard() {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/medico/pacientes-compartidos");
      setPacientes(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section
      className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
      data-testid="medico-pacientes-compartidos-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#0F2942] text-white grid place-items-center shrink-0">
            <FolderOpen size={20} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
              Personas usuarias compartidas
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-0.5">
              Expedientes autorizados por tus personas usuarias
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Solo aparecen aquí las personas usuarias que autorizaron el acceso desde su panel.
              El acceso es de solo lectura y puede ser revocado en cualquier momento.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando personas usuarias…
          </div>
        ) : pacientes.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
            data-testid="pacientes-compartidos-empty"
          >
            Aún no tienes personas usuarias que hayan compartido su expediente contigo.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="pacientes-compartidos-list">
            {pacientes.map((p) => (
              <li
                key={p.acceso_id}
                className="rounded-lg border border-border bg-white p-4 flex items-center gap-3"
                data-testid={`paciente-compartido-${p.patient_user_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#0F2942]/5 text-[#0F2942] grid place-items-center shrink-0">
                  <UserSquare2 size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0F2942] truncate">
                    {p.patient_nombre}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Calendar size={11} /> Compartió el{" "}
                    {new Date(p.created_at).toLocaleDateString("es-MX", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                </div>
                <Link to={`/medico/pacientes/${p.patient_user_id}`}>
                  <Button
                    size="sm"
                    className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                    data-testid={`paciente-compartido-open-${p.patient_user_id}`}
                  >
                    Ver expediente <ArrowRight size={12} />
                  </Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
